import { useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

export const useContent = (space = 'main') => {
  const [contents, setContents] = useState([]);
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({
    module_id: '',
    folder_id: '',
    title: '',
    content_type: 'blog',
    order: 1,
    generate_ai_image: false,
    content_data: {},
    is_public: false,
    is_listed: false,
    slug: '',
    unsplash_url: ''
  });
  const [photoOptions, setPhotoOptions] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [showUnsplashModal, setShowUnsplashModal] = useState(false);
  const [unsplashSearchQuery, setUnsplashSearchQuery] = useState('');
  const [unsplashResults, setUnsplashResults] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});
  const [viewPhotoModal, setViewPhotoModal] = useState(false);
  const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null);
  const [selectedPhotoAttribution, setSelectedPhotoAttribution] = useState(null);

  const fetchContents = async () => {
    try {
      const response = await axios.get(`${API}/module-contents?space=${space}`);
      setContents(response.data);
    } catch (error) {
      toast.error('Failed to load contents');
    }
  };

  const resetContentForm = () => {
    setContentForm({
      module_id: '',
      folder_id: '',
      title: '',
      content_type: 'blog',
      order: 1,
      generate_ai_image: false,
      content_data: {},
      is_public: false,
      is_listed: false,
      slug: '',
      unsplash_url: ''
    });
    setEditingContent(null);
    setPhotoOptions([]);
    setSelectedPhoto(null);
  };

  const handleEditContent = (content, scrollRef) => {
    setEditingContent(content.id);
    setContentForm({
      module_id: content.module_id,
      folder_id: content.folder_id || '',
      title: content.title,
      content_type: content.content_type,
      order: content.order,
      generate_ai_image: false,
      content_data: content.content_data || {},
      is_public: content.is_public || false,
      is_listed: content.is_listed || false,
      slug: content.slug || '',
      unsplash_url: ''
    });
    setPhotoOptions([]);
    setSelectedPhoto(null);
    scrollRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const createContent = async (onSuccess) => {
    if (!contentForm.folder_id || contentForm.folder_id === '') {
      toast.error('Please select a folder. Content must be organized in folders.');
      return;
    }
    
    let photoToUse = selectedPhoto;
    if (contentForm.unsplash_url && !selectedPhoto) {
      try {
        const response = await axios.post(`${API}/fetch-photo-from-url`, null, {
          params: { unsplash_url: contentForm.unsplash_url }
        });
        
        if (response.data.success) {
          photoToUse = response.data.photo;
          toast.success('Photo fetched from Unsplash!');
        } else {
          toast.error(response.data.message || 'Failed to fetch photo from URL');
          return;
        }
      } catch (error) {
        toast.error('Failed to fetch photo. Please check the URL.');
        return;
      }
    }
    
    const finalContentForm = { ...contentForm, space };
    if (photoToUse) {
      finalContentForm.content_data = {
        ...finalContentForm.content_data,
        ai_image: photoToUse.url,
        image_attribution: {
          photographer: photoToUse.photographer,
          photographer_url: photoToUse.photographer_url,
          unsplash_url: 'https://unsplash.com',
          description: photoToUse.description
        }
      };
      
      try {
        await axios.post(`${API}/trigger-photo-download`, null, {
          params: { download_location: photoToUse.download_location }
        });
      } catch (error) {
        console.error('Download tracking failed:', error);
      }
    }
    
    try {
      if (editingContent) {
        await axios.put(`${API}/module-contents/${editingContent}`, finalContentForm);
        toast.success('Content updated and assigned to folder!');
      } else {
        await axios.post(`${API}/module-contents`, finalContentForm);
        toast.success('Content created in folder!');
      }
      resetContentForm();
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save content');
    }
  };

  const fetchPhotoOptions = async () => {
    if (!contentForm.title) {
      toast.error('Please enter a content title first');
      return;
    }
    
    setLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/select-photo/${encodeURIComponent(contentForm.title)}`);
      if (response.data.success && response.data.photos.length > 0) {
        setPhotoOptions(response.data.photos);
        toast.success(`Found ${response.data.photos.length} photo options!`);
      } else {
        toast.error('No photos found for this title');
        setPhotoOptions([]);
      }
    } catch (error) {
      toast.error('Failed to fetch photo options');
      setPhotoOptions([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const searchUnsplash = async (query) => {
    if (!query) return;
    
    setLoadingPhotos(true);
    try {
      const response = await axios.get(`${API}/select-photo/${encodeURIComponent(query)}`);
      if (response.data.success && response.data.photos.length > 0) {
        setUnsplashResults(response.data.photos);
      } else {
        setUnsplashResults([]);
        toast.error('No photos found');
      }
    } catch (error) {
      toast.error('Failed to search Unsplash');
      setUnsplashResults([]);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const openUnsplashBrowser = () => {
    setUnsplashSearchQuery(contentForm.title || 'spiritual meditation');
    setShowUnsplashModal(true);
    searchUnsplash(contentForm.title || 'spiritual meditation');
  };

  const selectPhotoFromModal = (photo) => {
    setSelectedPhoto(photo);
    setShowUnsplashModal(false);
    toast.success('Photo selected!');
  };

  const handleDeleteContent = async (contentId, onSuccess) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;
    
    try {
      await axios.delete(`${API}/module-contents/${contentId}`);
      toast.success('Content deleted!');
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const toggleModuleExpansion = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  const getContentsByModule = (moduleId, allContents) => {
    return allContents.filter(c => c.module_id === moduleId).sort((a, b) => a.order - b.order);
  };

  return {
    contents,
    editingContent,
    contentForm,
    setContentForm,
    photoOptions,
    selectedPhoto,
    setSelectedPhoto,
    loadingPhotos,
    showUnsplashModal,
    setShowUnsplashModal,
    unsplashSearchQuery,
    setUnsplashSearchQuery,
    unsplashResults,
    expandedModules,
    viewPhotoModal,
    setViewPhotoModal,
    selectedPhotoUrl,
    setSelectedPhotoUrl,
    selectedPhotoAttribution,
    setSelectedPhotoAttribution,
    fetchContents,
    resetContentForm,
    handleEditContent,
    createContent,
    fetchPhotoOptions,
    searchUnsplash,
    openUnsplashBrowser,
    selectPhotoFromModal,
    handleDeleteContent,
    toggleModuleExpansion,
    getContentsByModule
  };
};

export default useContent;
